import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authContext';
import { Dialog as HeadlessDialog } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { PencilIcon } from '@heroicons/react/24/outline';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { sections } from '../data/journeyQuestions';
import { useNavigate } from 'react-router-dom';

const MySpiritualJourney = () => {
  const { currentUser } = useAuth();
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  // Initialize responses with empty arrays matching the sections structure
  const [responses, setResponses] = useState(() => 
    sections.map(section => new Array(section.questions.length).fill(""))
  );
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);

  // Helper function to get completed questions count
  const getCompletedQuestions = (sectionIndex) => {
    if (!responses || !responses[sectionIndex]) return 0;
    return responses[sectionIndex].filter(response => response.trim() !== "").length;
  };

  useEffect(() => {
    const loadResponses = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const docRef = doc(db, 'users', currentUser.uid, 'spiritualJourney', 'main');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const savedData = docSnap.data();
          const loadedResponses = sections.map((section, sIndex) => 
            section.questions.map((_, qIndex) => 
              savedData.responses?.[`${sIndex}_${qIndex}`] || ""
            )
          );
          setResponses(loadedResponses);
        }
      } catch (error) {
        console.error('Error loading responses:', error);
      }
    };

    loadResponses();
  }, [currentUser?.uid]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const userRef = collection(db, 'users');
        const q = query(userRef, where('uid', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          setUserData(userData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [currentUser?.uid]);

  const handleResponseChange = async (value) => {
    if (!responses) return;

    const newResponses = [...responses];
    newResponses[currentSection][currentQuestion] = value;
    setResponses(newResponses);

    if (!currentUser?.uid) return;

    try {
      setIsSaving(true);
      const docRef = doc(db, 'users', currentUser.uid, 'spiritualJourney', 'main');
      await setDoc(docRef, {
        responses: {
          [`${currentSection}_${currentQuestion}`]: value
        },
        lastPosition: {
          section: currentSection,
          question: currentQuestion,
          timestamp: new Date().toISOString()
        },
        lastUpdated: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Add these console logs to help debug
  console.log('All sections:', sections);
  console.log('Current section:', currentSection);
  console.log('Current question:', currentQuestion);

  if (!responses) return <div>Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Spiritual Journey</h1>
        <button
          onClick={() => {
            if (userData?.appID) {
              navigate(`/user/${userData.appID}?tab=journey`);
            }
          }}
          disabled={!userData?.appID}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50"
        >
          <span>View Story</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Section List - Left Side */}
        <div className="md:col-span-1">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Overall Progress</h2>
            <div className="space-y-6">
              {sections.map((section, index) => (
                <div 
                  key={index}
                  onClick={() => {
                    setCurrentSection(index);
                    setCurrentQuestion(0);
                  }}
                  className={`cursor-pointer transition-all duration-200 ${
                    currentSection === index 
                      ? 'transform scale-102' 
                      : 'opacity-75 hover:opacity-100'
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h3 className={`${
                      currentSection === index 
                        ? 'text-blue-600 font-semibold' 
                        : 'text-gray-700 font-medium'
                    } transition-colors duration-200`}>
                      {section.title}
                    </h3>
                    <span className={`text-sm ${
                      currentSection === index 
                        ? 'text-blue-600 font-medium' 
                        : 'text-gray-500'
                    }`}>
                      {getCompletedQuestions(index)} of {section.questions.length}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${
                        currentSection === index 
                          ? 'bg-blue-600' 
                          : 'bg-blue-400'
                      }`}
                      style={{ 
                        width: `${(getCompletedQuestions(index) / section.questions.length) * 100}%`
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Question Area - Right Side */}
        <div className="md:col-span-2">
          <div className="bg-white p-8 rounded-xl shadow-sm">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {sections[currentSection].title}
              </h2>
              <p className="text-gray-600">
                {sections[currentSection].description}
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                {sections[currentSection].questions[currentQuestion]}
              </h3>
              <textarea
                value={responses[currentSection][currentQuestion]}
                onChange={(e) => handleResponseChange(e.target.value)}
                className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Share your story..."
                rows={8}
              />
            </div>

            <div className="flex justify-between items-center">
              <button
                onClick={() => {
                  if (currentQuestion > 0) {
                    setCurrentQuestion(currentQuestion - 1);
                  } else if (currentSection > 0) {
                    setCurrentSection(currentSection - 1);
                    setCurrentQuestion(sections[currentSection - 1].questions.length - 1);
                  }
                }}
                disabled={currentSection === 0 && currentQuestion === 0}
                className="px-5 py-2.5 text-sm font-medium rounded-lg text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (currentQuestion < sections[currentSection].questions.length - 1) {
                    setCurrentQuestion(currentQuestion + 1);
                  } else if (currentSection < sections.length - 1) {
                    setCurrentSection(currentSection + 1);
                    setCurrentQuestion(0);
                  }
                }}
                className="px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm"
              >
                Next Question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MySpiritualJourney;